import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'

import { ChatThread } from './ChatThread'
import type { ChatMessage, LessonChatWidgetMeta } from '@/api/chat'
import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import { formatDateDMY } from '@/utils/date'

interface LessonChatHistoryDialogProps {
  meta: LessonChatWidgetMeta | null
  onClose: () => void
  /** Сообщить об ошибке (копирование текста) наружу — свой Alert модалка не держит. */
  onError?: (message: string) => void
}

/**
 * История переписки урока — только чтение, без композера и без реакций.
 *
 * Комната урока не входит в список диалогов, поэтому лента здесь грузится по
 * id из плашки. Прочитанными эти сообщения не помечаются: урок закончен, и
 * счётчик непрочитанных к нему уже не относится.
 */
export function LessonChatHistoryDialog({
  meta,
  onClose,
  onError,
}: LessonChatHistoryDialogProps) {
  const { t } = useTranslation()
  const currentUserId = useAuthStore((s) => s.user?.id ?? 0)
  const fetchMessages = useChatStore((s) => s.fetchMessages)

  /**
   * Последняя открытая плашка держится до конца анимации закрытия: обнули её
   * сразу — и на время ухода модалки заголовок опустеет, а лента покажет
   * «сообщений нет».
   */
  const [shownMeta, setShownMeta] = useState<LessonChatWidgetMeta | null>(meta)
  useEffect(() => {
    if (meta !== null) setShownMeta(meta)
  }, [meta])

  const roomId = shownMeta?.lesson_chat_room_id ?? null

  // Имя автора — только если в переписке урока больше двух участников:
  // в паре учитель-ученик подпись над каждым сообщением ничего не добавляет.
  const showAuthor = useChatStore((s) => {
    const messages = roomId === null ? [] : (s.byRoom[roomId]?.messages ?? [])

    return new Set(messages.map((message) => message.author_user_id)).size > 2
  })

  // История урока неизменна, но модалку открывают и после первого просмотра:
  // перечитываем, чтобы не показать обрезанный кусок, оставшийся в сторе от
  // предыдущего открытия. Первую загрузку делает сама лента, поэтому здесь
  // только обновление уже загруженной.
  useEffect(() => {
    if (meta === null || roomId === null) return
    if (!useChatStore.getState().byRoom[roomId]?.loaded) return
    void fetchMessages(roomId)
  }, [meta, roomId, fetchMessages])

  const noop = useCallback(() => {}, [])

  const handleCopyText = useCallback(
    (message: ChatMessage) => {
      const value = message.text ?? ''
      if (!value.trim()) return
      if (typeof navigator.clipboard?.writeText !== 'function') {
        onError?.(t('chat.copyFailed'))
        return
      }
      navigator.clipboard.writeText(value).catch(() => onError?.(t('chat.copyFailed')))
    },
    [onError, t]
  )

  return (
    <Dialog
      open={meta !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setShownMeta(null) }}
    >
      {/* Пояснение про режим чтения — в шапке: под лентой его видно только
          после прокрутки до самого низа, то есть когда оно уже не нужно */}
      <DialogTitle sx={{ pb: 1 }}>
        {shownMeta
          ? t('chat.lessonWidget.title', {
              id: shownMeta.lesson_id,
              date: formatDateDMY(shownMeta.lesson_date),
            })
          : ''}
        <Typography variant="caption" color="text.secondary" display="block">
          {t('chat.lessonChatReadonly')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0, minHeight: 240 }}>
        <ChatThread
          roomId={roomId}
          showAuthor={showAuthor}
          canWrite={false}
          readOnly
          autoLoadAll
          hideDateDividers
          currentUserId={currentUserId}
          onEdit={noop}
          onDelete={noop}
          onCopyText={handleCopyText}
          onToggleReaction={noop}
          autoMarkRead={false}
          emptyText={t('chat.noMessages')}
        />
      </DialogContent>
    </Dialog>
  )
}
